import React, { useState } from 'react';
import { TrendingUp, BarChart2, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ChartVisualization from './ChartVisualization';

const ReportCard = ({ report, categoryColor, formatDate }) => {
  const [showPreview, setShowPreview] = useState(false);

  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100',
    green: 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100',
    purple: 'border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100',
    orange: 'border-orange-500 bg-orange-50 text-orange-700 hover:bg-orange-100'
  };

  const buttonColors = {
    blue: 'bg-blue-500 hover:bg-blue-600 text-white',
    green: 'bg-green-500 hover:bg-green-600 text-white',
    purple: 'bg-purple-500 hover:bg-purple-600 text-white',
    orange: 'bg-orange-500 hover:bg-orange-600 text-white'
  };

  const summaryBgColors = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    orange: 'bg-orange-50 border-orange-200'
  };

  const hasSummary = report?.summary && report?.summary?.totalOrders > 0;

  return (
    <motion.div 
      className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="p-6">
        <motion.div 
          className="flex items-start justify-between mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {report?.title}
              </h3>
              {report?.comingSoon && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                  Coming Soon
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {report?.description}
            </p>
          </div>
          <motion.div
            whileHover={{ rotate: 360, scale: 1.1 }}
            transition={{ duration: 0.5 }}
          >
            <BarChart2 className={`w-8 h-8 text-${categoryColor}-500 flex-shrink-0 ml-4`} />
          </motion.div>
        </motion.div>

        {/* Real data summary strip (Purchase Analytics only) */}
        {hasSummary && (
          <motion.div
            className={`mb-4 p-3 rounded-lg border ${summaryBgColors?.[categoryColor]}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Live Summary</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs text-gray-500">Orders</p>
                <p className="text-sm font-bold text-gray-900">{report?.summary?.totalOrders}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Spend</p>
                <p className="text-sm font-bold text-gray-900">
                  {report?.summary?.totalSpend > 0
                    ? `₹${(report?.summary?.totalSpend / 100000)?.toFixed(1)}L`
                    : '₹0'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Avg Price/T</p>
                <p className="text-sm font-bold text-gray-900">
                  {report?.summary?.avgPrice > 0 ? `₹${report?.summary?.avgPrice?.toLocaleString('en-IN')}` : '—'}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Metrics */}
        <motion.div 
          className="mb-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-xs font-medium text-gray-500 mb-2">KEY METRICS</p>
          <div className="flex flex-wrap gap-2">
            {report?.metrics?.map((metric, index) => (
              <motion.span
                key={index}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${colorClasses?.[categoryColor]}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <TrendingUp className="w-3 h-3" />
                {metric}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Chart Type */}
        <motion.div 
          className="mb-6 pb-4 border-b border-gray-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-xs font-medium text-gray-500 mb-1">VISUALIZATION</p>
          <p className="text-sm text-gray-700 capitalize">{report?.chartType?.replace('-', ' ')} Chart</p>
        </motion.div>

        {/* Preview Button */}
        <motion.button
          onClick={() => setShowPreview(!showPreview)}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg transition-all font-medium ${buttonColors?.[categoryColor]}`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Eye className="w-5 h-5" />
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </motion.button>
      </div>
      {/* Preview Section */}
      <AnimatePresence>
        {showPreview && (
          <motion.div 
            className="border-t border-gray-200 p-6 bg-gray-50"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChartVisualization
              chartType={report?.chartType}
              title={report?.title}
              color={categoryColor}
              realData={report?.realData}
              comingSoon={report?.comingSoon}
              valueUnit={report?.valueUnit || 'currency'}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ReportCard;